(() => {
  if (customElements.get('wolf-collection-filters')) return;

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  class WolfCollectionFilters extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true });
      } else {
        this.initialize();
      }
    }

    disconnectedCallback() {
      if (this.onPopState) window.removeEventListener('popstate', this.onPopState);
      if (this.onPromotionsReady) window.removeEventListener('wolf:plp-promotions:ready', this.onPromotionsReady);
      this.controlEvents?.abort();
      this.requestId = (this.requestId || 0) + 1;
    }

    async initialize() {
      this.api = window.WolfColors;
      if (!this.api) return;
      try {
        this.config = JSON.parse(this.querySelector('[data-filter-config]').textContent);
        this.wrapper = this.closest('[data-wolf-collection]');
        this.originalGrid = this.wrapper.querySelector('#ProductGridContainer');
        this.bar = this.querySelector('[data-filter-bar]');
        this.status = this.querySelector('[data-filter-status]');
        this.clear = this.querySelector('[data-filter-clear]');
        this.activeFilters = this.querySelector('[data-filter-active]');
        this.wrapper.addEventListener('click', (event) => this.selectCardSwatch(event), true);
        this.wrapper.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') this.selectCardSwatch(event);
        }, true);
        this.colorFilter = this.config.filters.find((filter) =>
          /^(color|colour|glaze)$/i.test(filter.label) || /\.option\.(color|colour|glaze)$/.test(filter.param)
        );
        this.formFilter = this.config.filters.find((filter) =>
          filter.param === 'filter.p.product_type' || filter.param === 'filter.p.type'
        );
        // Build Your Set groups use product tags, independently of Shopify types.
        if (this.config.cardMode === 'set-builder') this.formFilter = null;
        this.colors = this.colorFilter?.values || [];
        this.forms = this.formFilter?.values || [];
        this.readState();
        if (this.needsCatalog()) {
          this.originalGrid.hidden = true;
          this.status.textContent = 'Finding matching pieces…';
          this.hidden = false;
        }
        this.clear.addEventListener('click', () => {
          this.change({ color: '', form: '' });
          this.querySelector('.wolf-collection-filters__trigger')?.focus({ preventScroll: true });
        });
        this.onPopState = () => {
          this.readState();
          this.apply();
        };
        window.addEventListener('popstate', this.onPopState);
        // The theme editor can load these section scripts in either order.
        this.onPromotionsReady = () => this.decorateResults();
        window.addEventListener('wolf:plp-promotions:ready', this.onPromotionsReady);

        if (!this.colorFilter || !this.formFilter) {
          // Fetch every collection page before exposing choices or calculating totals.
          // Filtering only the rendered grid would silently omit later-page matches.
          this.discoveryCatalog = await this.api.loadCatalog(this.config.collectionUrl);
          if (!this.isConnected) return;
          if (!this.colorFilter) this.colors = this.catalogColors(this.discoveryCatalog);
          if (!this.formFilter) this.forms = this.catalogForms(this.discoveryCatalog);
        }
        this.buildBar();
        this.hidden = false;
        this.wrapper.classList.add('wolf-collection--enhanced');
        await this.apply();
      } catch (error) {
        this.fail(error);
      }
    }

    readState() {
      const params = new URL(window.location.href).searchParams;
      const nativeColor = this.colorFilter?.values.find((value) =>
        params.getAll(this.colorFilter.param).includes(String(value.value))
      );
      const nativeForm = this.formFilter?.values.find((value) =>
        params.getAll(this.formFilter.param).includes(String(value.value))
      );
      this.state = {
        color: params.get('wc_color') || nativeColor?.label || '',
        form: params.get('wc_form') || nativeForm?.label || '',
        sort: params.get('sort_by') || this.config.defaultSort,
        page: Math.max(1, Number.parseInt(params.get('wc_page') || '1', 10) || 1),
      };
    }

    selectCardSwatch(event) {
      const swatch = event.target.closest('[data-wolf-variant-url]');
      if (!swatch) return;
      // These cards update their purchasing variant through their own handler.
      if (swatch.closest('wolf-set-builder-card')) return;
      const card = swatch.closest('.card');
      if (!card) return;
      event.preventDefault();
      event.stopPropagation();
      const variantId = swatch.getAttribute('for');
      card.querySelectorAll('.media.variant').forEach((media) =>
        media.classList.toggle('visible', media.classList.contains(`variantId-${variantId}`))
      );
      card.querySelectorAll('.variant-swatch').forEach((label) => {
        label.classList.toggle('visible', label === swatch);
        label.setAttribute('aria-pressed', String(label === swatch));
      });
      card.querySelectorAll('a').forEach((link) => { link.href = swatch.dataset.wolfVariantUrl; });
      const quickAdd = card.querySelector('[data-product-url]');
      if (quickAdd) quickAdd.dataset.productUrl = swatch.dataset.wolfVariantUrl;
      const price = card.querySelector('.price');
      if (price) {
        price.classList.toggle('price--on-sale', Boolean(swatch.dataset.wolfVariantCompare));
        price.querySelectorAll('.price__regular .price-item, .price-item--sale').forEach((node) => {
          node.textContent = swatch.dataset.wolfVariantPrice;
        });
        const compare = price.querySelector('.price__sale .price-item--regular');
        if (compare) compare.textContent = swatch.dataset.wolfVariantCompare;
      }
    }

    catalogColors(products) {
      const values = new Map();
      products.forEach((product) => {
        const productColors = new Set();
        product.variants.forEach((variant) => {
          if (!variant.color) return;
          const key = this.api.normalizeColor(variant.color);
          if (!values.has(key)) values.set(key, {
            label: variant.color, value: variant.color, count: 0,
            swatch: variant.swatch, swatchImage: variant.swatchImage, brandColor: variant.brandColor,
          });
          if (!productColors.has(key)) values.get(key).count += 1;
          productColors.add(key);
        });
      });
      return Array.from(values.values());
    }

    catalogForms(products) {
      const values = new Map();
      products.forEach((product) => {
        if (!product.variants.length) return;
        this.productForms(product).forEach((form) => {
          if (!values.has(form)) values.set(form, { label: form, value: form, count: 0 });
          values.get(form).count += 1;
        });
      });
      return Array.from(values.values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    productForms(product) {
      if (this.config.cardMode === 'set-builder') {
        return ['Plates', 'Bowls', 'Servingware'].filter((group) => product.tags?.includes(group === 'Servingware' ? 'BYS-Servingware' : group));
      }
      if (this.formFilter) return product.type ? [product.type] : [];
      // This catalog historically stores its forms as tags, with many empty
      // product types. Include clear title nouns for untagged serving bowls, etc.
      const labels = [
        ['Bowls', /\bbowls?\b/i], ['Plates', /\bplates?\b/i],
        ['Mugs', /\bmugs?\b/i], ['Cups', /\bcups?\b/i],
        ['Vases', /\bvases?\b/i], ['Planters', /\bplanters?\b/i],
        ['Servingware', /^servingware$/i], ['Sets', /\bsets?\b|^bundles$|place setting/i],
      ];
      const tags = Array.isArray(product.tags) ? product.tags : [];
      return labels.filter(([, pattern]) =>
        [product.title, product.type, ...tags].some((value) => pattern.test(value || ''))
      ).map(([label]) => label);
    }

    buildBar() {
      this.bar.replaceChildren(element('span', 'wolf-collection-filters__label', 'Filter'));
      if (this.config.showFiltering !== false) {
        this.addGroup('color', 'Color', this.colors);
        this.addGroup('form', 'Form', this.forms);
      }
      if (this.config.showSorting) {
        const label = element('label', 'wolf-collection-filters__sort');
        label.append(element('span', 'visually-hidden', 'Sort products'));
        const visibleLabel = element('span', 'wolf-collection-filters__sort-label', 'Sort By');
        visibleLabel.setAttribute('aria-hidden', 'true');
        visibleLabel.append(element('span', 'wolf-collection-filters__sort-current'));
        const caret = element('span', 'wolf-collection-filters__sort-caret');
        caret.setAttribute('aria-hidden', 'true');
        const caretTemplate = this.querySelector('[data-sort-caret]');
        if (caretTemplate) caret.append(caretTemplate.content.cloneNode(true));
        const select = element('select');
        select.name = 'sort_by';
        select.setAttribute('aria-label', 'Sort products');
        const sortLabels = {
          'best-selling': 'Best Seller',
          'price-ascending': 'Price Low to High',
          'price-descending': 'Price High to Low',
          'created-descending': 'Newest',
        };
        this.config.sortOptions.forEach((option) => {
          const choice = element('option', '', sortLabels[option.value] || option.name);
          choice.value = option.value;
          select.append(choice);
        });
        select.value = this.state.sort;
        select.addEventListener('change', () => this.change({ sort: select.value }));
        label.append(visibleLabel, caret, select);
        this.bar.append(label);
      }
      this.bindGroupControls();
      this.updateControls();
    }

    closeGroups(except = null) {
      this.querySelectorAll('.wolf-collection-filters__group[open]').forEach((group) => {
        if (group !== except) group.open = false;
      });
    }

    bindGroupControls() {
      this.controlEvents?.abort();
      this.controlEvents = new AbortController();
      const options = { signal: this.controlEvents.signal };
      document.addEventListener('click', (event) => {
        this.closeGroups(event.target.closest('.wolf-collection-filters__group'));
      }, options);
      document.addEventListener('focusin', (event) => {
        this.closeGroups(event.target.closest('.wolf-collection-filters__group'));
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const group = event.target.closest('.wolf-collection-filters__group[open]');
        if (!group) return;
        event.preventDefault();
        group.open = false;
        group.querySelector('summary').focus({ preventScroll: true });
      }, options);
    }

    addGroup(key, label, values) {
      if (!values.length) return;
      const group = element('details', `wolf-collection-filters__group wolf-collection-filters__group--${key}`);
      group.dataset.filterGroup = key;
      const trigger = element('summary', 'wolf-collection-filters__trigger', label);
      trigger.setAttribute('aria-label', `Filter by ${label.toLowerCase()}`);
      const caret = element('span', 'wolf-collection-filters__group-caret');
      caret.setAttribute('aria-hidden', 'true');
      const caretTemplate = this.querySelector('[data-sort-caret]');
      if (caretTemplate) caret.append(caretTemplate.content.cloneNode(true));
      trigger.append(caret);
      group.append(trigger);
      group.addEventListener('toggle', () => {
        if (group.open) this.closeGroups(group);
      });
      const panel = element('div', 'wolf-collection-filters__panel');
      panel.setAttribute('role', 'group');
      panel.setAttribute('aria-label', `${label} choices`);
      const options = element('div', 'wolf-collection-filters__options');
      const all = this.filterButton(key, '', `All ${label.toLowerCase()}s`);
      all.textContent = `All ${label.toLowerCase()}s`;
      options.append(all);
      [...values].sort((a, b) => a.label.localeCompare(b.label)).forEach((value) => {
        const button = this.filterButton(key, value.label, value.label);
        if (key === 'color') {
          button.classList.add('wolf-collection-filters__option--color');
          const dot = element('span', 'wolf-collection-filters__dot');
          dot.setAttribute('aria-hidden', 'true');
          const style = this.api.colorStyle(value.label, null, value.brandColor);
          // Swatches show the actual glaze; the display metafield only supplies a fallback.
          dot.style.setProperty('--swatch-color', value.swatch || style.background);
          if (value.swatchImage) dot.style.backgroundImage = `url("${String(value.swatchImage).replace(/["\\\n\r]/g, '')}")`;
          button.append(dot, element('span', 'wolf-collection-filters__option-label', value.label));
        } else {
          button.textContent = value.label;
        }
        options.append(button);
      });
      panel.append(options);
      group.append(panel);
      this.bar.append(group);
    }

    filterButton(key, value, label) {
      const button = element('button', 'wolf-collection-filters__option');
      button.type = 'button';
      button.dataset.filterKey = key;
      button.dataset.filterValue = value;
      button.setAttribute('aria-label', label);
      button.addEventListener('click', () => {
        const group = button.closest('.wolf-collection-filters__group');
        if (group) {
          group.open = false;
          group.querySelector('summary').focus({ preventScroll: true });
        }
        this.change({ [key]: this.state[key] === value ? '' : value });
      });
      return button;
    }

    updateControls() {
      this.querySelectorAll('[data-filter-key]').forEach((button) => {
        const active = this.api.normalizeColor(button.dataset.filterValue) ===
          this.api.normalizeColor(this.state[button.dataset.filterKey]);
        button.setAttribute('aria-pressed', String(active));
      });
      const sort = this.querySelector('select');
      if (sort) {
        sort.value = this.state.sort;
        const current = this.querySelector('.wolf-collection-filters__sort-current');
        const showSelection = new URL(window.location.href).searchParams.has('sort_by');
        if (current) current.textContent = showSelection && sort.selectedOptions[0]
          ? ` ${sort.selectedOptions[0].textContent}` : '';
      }
      this.clear.hidden = !this.state.color && !this.state.form;
      this.updateSelectedFilters();
    }

    updateSelectedFilters() {
      if (!this.activeFilters) return;
      this.activeFilters.replaceChildren();
      ['color', 'form'].forEach((key) => {
        const value = this.state[key];
        if (!value) return;
        const chip = element('button', 'wolf-collection-filters__chip');
        chip.type = 'button';
        chip.setAttribute('aria-label', `Remove ${key} filter: ${value}`);
        chip.append(element('span', '', value));
        const icon = element('span', 'wolf-collection-filters__chip-remove');
        icon.setAttribute('aria-hidden', 'true');
        const template = this.querySelector('[data-filter-remove-icon]');
        if (template) icon.append(template.content.cloneNode(true));
        else icon.textContent = '×';
        chip.append(icon);
        chip.addEventListener('click', () => {
          this.change({ [key]: '' });
          this.querySelector(`[data-filter-group="${key}"] summary`)?.focus({ preventScroll: true });
        });
        this.activeFilters.append(chip);
      });
      this.activeFilters.hidden = !this.activeFilters.children.length;
    }

    nativeValue(key) {
      const filter = key === 'color' ? this.colorFilter : this.formFilter;
      return filter?.values.find((value) =>
        this.api.normalizeColor(value.label) === this.api.normalizeColor(this.state[key])
      );
    }

    needsCatalog() {
      return Boolean((this.state.color && !this.nativeValue('color')) ||
        (this.state.form && !this.nativeValue('form')));
    }

    change(values) {
      Object.assign(this.state, values, { page: 1 });
      const url = new URL(window.location.href);
      ['wc_color', 'wc_form', 'wc_page', 'page'].forEach((key) => url.searchParams.delete(key));
      if (this.colorFilter) url.searchParams.delete(this.colorFilter.param);
      if (this.formFilter) url.searchParams.delete(this.formFilter.param);
      url.searchParams.set('sort_by', this.state.sort);
      const fallback = this.needsCatalog();
      ['color', 'form'].forEach((key) => {
        if (!this.state[key]) return;
        const native = this.nativeValue(key);
        const filter = key === 'color' ? this.colorFilter : this.formFilter;
        url.searchParams.set(fallback ? `wc_${key}` : filter.param, fallback ? this.state[key] : native.value);
      });
      if (fallback) {
        window.history.pushState({}, '', url);
        this.apply();
      } else {
        // Native filtering keeps Shopify's counts, ordering and pagination authoritative.
        window.location.assign(url.toString());
      }
    }

    async apply() {
      if (!this.isConnected) return;
      const requestId = this.requestId = (this.requestId || 0) + 1;
      this.updateControls();
      const params = new URL(window.location.href).searchParams;
      if (!this.needsCatalog()) {
        if (params.has('wc_color') || params.has('wc_form')) {
          // Resolve human-readable links to Shopify's current filter value (which may be a GID).
          this.change({});
          return;
        }
        if (this.results) this.results.hidden = true;
        this.originalGrid.hidden = false;
        this.setStatus(this.config.count);
        return;
      }

      this.status.textContent = 'Finding matching pieces…';
      this.originalGrid.hidden = true;
      this.setAttribute('aria-busy', 'true');
      if (this.results) this.results.setAttribute('aria-busy', 'true');
      try {
        const catalogUrl = new URL(this.config.collectionUrl, window.location.origin);
        // Keep Shopify in charge of best-selling/date/manual ordering and unrelated native filters.
        params.forEach((value, key) => {
          if (key === 'sort_by' || (key.startsWith('filter.') &&
            key !== this.colorFilter?.param && key !== this.formFilter?.param)) catalogUrl.searchParams.append(key, value);
        });
        const url = catalogUrl.pathname + catalogUrl.search;
        const [products, cards] = await Promise.all([
          this.api.loadCatalog(url),
          this.usesCollectionCards() ? window.WolfCollectionCardResults.load(url, this.config.sectionId) : null,
        ]);
        if (requestId !== this.requestId || !this.isConnected) return;
        this.resultCards = cards;
        const color = this.api.normalizeColor(this.state.color);
        const form = this.api.normalizeColor(this.state.form);
        this.matches = products.flatMap((product) => {
          if (this.config.cardMode === 'set-builder' && !this.productForms(product).length) return [];
          if (form && !this.productForms(product).some((value) => this.api.normalizeColor(value) === form)) return [];
          const variants = product.variants.filter((variant) =>
            !color || this.api.normalizeColor(variant.color) === color
          );
          const variant = variants.find((value) => value.available) || variants[0];
          return variant ? [{ product, variant }] : [];
        });
        this.renderResults();
      } catch (error) {
        if (requestId === this.requestId) this.fail(error);
      } finally {
        if (requestId === this.requestId) {
          this.removeAttribute('aria-busy');
          if (this.results) this.results.removeAttribute('aria-busy');
        }
      }
    }

    renderResults() {
      if (!this.results) {
        this.results = element('div', 'wolf-collection__results page-width');
        this.results.setAttribute('tabindex', '-1');
        this.results.setAttribute('aria-label', 'Filtered products');
        this.originalGrid.after(this.results);
      }
      this.results.hidden = false;
      this.originalGrid.hidden = true;
      this.results.replaceChildren();
      const pageCount = Math.max(1, Math.ceil(this.matches.length / this.config.pageSize));
      this.state.page = Math.min(this.state.page, pageCount);
      const offset = (this.state.page - 1) * this.config.pageSize;
      const matches = this.matches.slice(offset, offset + this.config.pageSize);
      if (this.matches.length) {
        if (this.config.cardMode === 'set-builder') {
          ['Plates', 'Bowls', 'Servingware'].forEach((group) => {
            const groupMatches = matches.filter(({ product }) => product.tags?.includes(group === 'Servingware' ? 'BYS-Servingware' : group));
            if (!groupMatches.length) return;
            const heading = element('h2', 'category-header', group);
            heading.id = `filtered-${this.config.sectionId}-${group.toLowerCase()}`;
            heading.dataset.wolfFilterGroup = group.toLowerCase();
            this.results.append(heading);
            this.appendResultGrid(groupMatches, group.toLowerCase());
          });
        } else this.appendResultGrid(matches);
        this.decorateResults();
      }
      else this.results.append(element('p', 'wolf-collection__empty', 'No pieces match these filters. Try another color or form.'));
      if (pageCount > 1) {
        const navigation = element('nav', 'wolf-collection__pagination');
        navigation.setAttribute('aria-label', 'Product pages');
        const previous = element('button', '', 'Previous');
        previous.type = 'button';
        previous.disabled = this.state.page === 1;
        previous.addEventListener('click', () => this.pageTo(this.state.page - 1));
        const next = element('button', '', 'Next');
        next.type = 'button';
        next.disabled = this.state.page === pageCount;
        next.addEventListener('click', () => this.pageTo(this.state.page + 1));
        navigation.append(previous, element('span', '', `${this.state.page} / ${pageCount}`), next);
        this.results.append(navigation);
      }
      this.setStatus(this.matches.length);
    }

    usesCollectionCards() {
      return this.config.cardMode === 'set-builder' || this.config.cardMode === 'registry';
    }

    appendResultGrid(matches, group = 'products') {
      const grid = element('ul', `grid product-grid grid--${this.config.columnsMobile}-col-tablet-down grid--${this.config.columnsDesktop}-col-desktop`);
      const cards = matches.map((match) => {
        const card = this.usesCollectionCards()
          ? window.WolfCollectionCardResults.renderCard(this.resultCards, match, `filtered-${group}`)
          : this.api.renderCard(match);
        card.classList.add('grid__item');
        if (!this.usesCollectionCards() && !match.variant.available) card.append(element('span', 'badge', 'Sold out'));
        grid.append(card);
        return { card, match };
      });
      this.results.append(grid);
      if (this.config.cardMode === 'set-builder') {
        cards.forEach(({ card, match }) => window.WolfCollectionCardResults.selectColor(card, match, this.api.normalizeColor));
      }
    }

    decorateResults() {
      if (!this.results || this.results.hidden) return;
      const grid = this.results.querySelector('.product-grid');
      if (!grid) return;
      window.WolfPlpPromotions?.decorateGrid(grid, {
        // This grid contains only the custom color/form filter results.
        filtered: true,
        page: this.state.page,
        columnsDesktop: this.config.columnsDesktop,
        columnsMobile: this.config.columnsMobile,
      });
    }

    pageTo(page) {
      this.state.page = page;
      const url = new URL(window.location.href);
      if (page > 1) url.searchParams.set('wc_page', page);
      else url.searchParams.delete('wc_page');
      window.history.pushState({}, '', url);
      this.renderResults();
      this.results.focus({ preventScroll: true });
      this.scrollIntoView({ block: 'start', behavior: 'auto' });
    }

    setStatus(count) {
      this.status.textContent = `${count} ${count === 1 ? 'piece' : 'pieces'}`;
      // The hero and native desktop facets share an ID; update every count,
      // including the separate mobile labels, so resizing cannot show stale totals.
      document.querySelectorAll('#ProductCountDesktop, #ProductCount, .mobile-facets__count').forEach((node) => {
        node.textContent = `${count} ${count === 1 ? 'product' : 'products'}`;
      });
    }

    fail(error) {
      console.warn('Color and form filters could not load.', error);
      if (!this.state?.color && !this.state?.form) {
        this.hidden = true;
        this.wrapper?.classList.remove('wolf-collection--enhanced');
        return;
      }
      // A failed deep link must not quietly display unfiltered products as matches.
      this.hidden = false;
      if (this.originalGrid) this.originalGrid.hidden = true;
      if (this.results) this.results.hidden = true;
      this.status.textContent = 'These pieces could not load. Please refresh, or clear your filters to browse the collection.';
      this.clear.hidden = false;
    }
  }

  customElements.define('wolf-collection-filters', WolfCollectionFilters);
})();
